import React, { Fragment, type ReactNode } from 'react';

import { useFelaEnhanced } from '@ackee/fela';

import { Offices } from 'modules/offices';
import { Title, TitleTypes, Text, TextTypes, Divider, DynamicContainer } from 'modules/ui';
import { InquirySection } from 'modules/inquiry';

import { useIntl } from 'modules/ui/dependencies';

import type { BlogPost, BlogPostsPageContext, PostAuthor, PostCategory } from '../../types';
import { CategoriesList } from '../CategoriesList';
import { Pagination } from '../Pagination';
import { BlogPostsList } from '../BlogPostsList';

import * as felaRules from './BlogPostsPage.rules';
import { OtherLangTitleLink } from './OtherLangsTitle';

export interface BlogPostsPageProps {
    title?: ReactNode;
    description?: ReactNode;
    author?: PostAuthor;
    posts: BlogPost[];
    categories?: PostCategory[];
    showAllCategory?: boolean;
    basePath: string;
    pageContext: BlogPostsPageContext;
    csPosts?: BlogPost[];
    enPosts?: BlogPost[];
    dePosts?: BlogPost[];
    displayOtherLanguages?: boolean;
    locale?: string;
    currentPageTitle?: string;
}

interface FullContent {
    content: BlogPost[] | undefined;
    link: string;
    lang: 'cs' | 'en' | 'de';
}

export const BlogPostsPage = ({
    title,
    description,
    categories,
    posts,
    showAllCategory = false,
    basePath,
    pageContext,
    displayOtherLanguages = false,
    csPosts,
    enPosts,
    dePosts,
    author,
    currentPageTitle,
}: BlogPostsPageProps) => {
    const { locale } = useIntl();
    const { theme, styles, rules } = useFelaEnhanced(felaRules);
    const categoryActiveStyle = {
        color: theme.colors.fuchsia,
        textDecorationColor: theme.colors.blue,
    };
    const otherLocales = ['cs', 'en', 'de'].filter(item => item !== locale);

    const otherLocalesFullContent: Record<string, FullContent> = {
        cs: { lang: 'cs', content: csPosts, link: `https://www.ackee.cz/blog/author/${csPosts?.[0]?.author.slug}` },
        en: { lang: 'en', content: enPosts, link: `https://www.ackee.agency/blog/author/${enPosts?.[0]?.author.slug}` },
        de: { lang: 'de', content: dePosts, link: `https://www.ackee.de/blog/author/${dePosts?.[0]?.author.slug}` },
    };

    return (
        <>
            <DynamicContainer isFluid extend={{ container: rules.container }}>
                {author ? (
                    <div className={styles.authorWrapper}>
                        <div className={styles.authorContainer}>
                            <img alt={author.displayName} src={author.photo?.url} className={styles.authorImage} />

                            <div className={styles.authorTextContainer}>
                                {/* @ts-expect-error */}
                                <Title type={TitleTypes.H1_ITALIC} as={TitleTypes.H1} extend={{ title: rules.title }}>
                                    {author.displayName}
                                </Title>
                                {/* @ts-expect-error */}
                                <Text as={TextTypes.PARAGRAPH} type={TextTypes.LARGE} extend={{ text: rules.role }}>
                                    {author.role}
                                </Text>
                                {author.description && (
                                    // @ts-expect-error
                                    <Text
                                        as={TitleTypes.H2}
                                        type={TextTypes.NORMAL}
                                        extend={{ text: rules.description }}
                                    >
                                        {author.description}
                                    </Text>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/** @ts-expect-error */}
                        <Title type={TitleTypes.H1_ITALIC} as={TitleTypes.H1} extend={{ title: rules.categoryTitle }}>
                            {title} {/* @ts-expect-error */}
                            <Text
                                as={TitleTypes.H2}
                                type={TextTypes.NORMAL}
                                extend={{ text: rules.categoryTitleNumber }}
                            >
                                {currentPageTitle}
                            </Text>
                        </Title>
                        {description && (
                            /** @ts-expect-error */
                            <Text as={TitleTypes.H2} type={TextTypes.NORMAL} extend={{ text: rules.description }}>
                                {description}
                            </Text>
                        )}
                        {/** @ts-expect-error */}
                        <Divider />
                    </>
                )}
                {/* @ts-expect-error */}
                <Divider />

                {categories && (
                    <CategoriesList
                        categories={categories}
                        showAllCategory={showAllCategory}
                        activeStyle={categoryActiveStyle}
                    />
                )}

                {displayOtherLanguages ? (
                    <>
                        <BlogPostsList posts={posts} />
                        <div style={{ padding: '1rem' }}>
                            <Pagination basePath={basePath} {...pageContext} />
                        </div>

                        {otherLocales.map(otherLocale => {
                            if (!otherLocalesFullContent[otherLocale]?.content?.length) {
                                return null;
                            }

                            return (
                                <Fragment key={otherLocale}>
                                    <OtherLangTitleLink
                                        link={otherLocalesFullContent[otherLocale].link}
                                        lang={otherLocalesFullContent[otherLocale].lang}
                                    />
                                    {/* @ts-expect-error */}
                                    <Divider />
                                    <BlogPostsList
                                        posts={otherLocalesFullContent[otherLocale].content!}
                                        languageMutation={otherLocalesFullContent[otherLocale].lang}
                                    />
                                </Fragment>
                            );
                        })}
                    </>
                ) : (
                    <>
                        <BlogPostsList posts={posts} />
                        <Pagination basePath={basePath} {...pageContext} />
                    </>
                )}
            </DynamicContainer>

            <Offices lazyLoaded />
            <InquirySection />
        </>
    );
};
